// Rate limiting utilities
import { SECURITY_CONFIG } from '@/config/security'

interface RateLimitEntry {
  count: number
  resetTime: number
  blocked: boolean
  blockUntil?: number
}

interface RateLimitOptions {
  maxAttempts: number
  windowMs: number
  blockDurationMs?: number
  keyGenerator?: (identifier: string) => string
}

class RateLimiter {
  private static instance: RateLimiter
  private limits: Map<string, RateLimitEntry> = new Map()
  private cleanupInterval: number | null = null

  private constructor() {
    this.startCleanup()
  }

  static getInstance(): RateLimiter {
    if (!RateLimiter.instance) {
      RateLimiter.instance = new RateLimiter()
    }
    return RateLimiter.instance
  }

  private startCleanup(): void {
    // Clean up expired entries every 5 minutes
    this.cleanupInterval = window.setInterval(() => {
      this.cleanup()
    }, 5 * 60 * 1000)
  }

  private cleanup(): void {
    const now = Date.now()
    const expiredKeys: string[] = []
    
    for (const [key, entry] of this.limits.entries()) {
      if (now > entry.resetTime) {
        expiredKeys.push(key)
      }
    }
    
    expiredKeys.forEach(key => this.limits.delete(key))
  }

  private generateKey(identifier: string, type: string): string {
    return `${type}:${identifier}`
  }

  isAllowed(
    identifier: string, 
    type: 'login' | 'api' | 'form' | 'file' | 'general',
    options?: Partial<RateLimitOptions>
  ): { allowed: boolean; remaining: number; resetTime: number; retryAfter?: number } {
    const config = this.getConfig(type, options)
    const key = this.generateKey(identifier, type)
    const now = Date.now()
    
    let entry = this.limits.get(key)
    
    if (!entry) {
      entry = {
        count: 0,
        resetTime: now + config.windowMs,
        blocked: false
      }
      this.limits.set(key, entry)
    }
    
    // Check if currently blocked
    if (entry.blocked && entry.blockUntil && now < entry.blockUntil) {
      return {
        allowed: false,
        remaining: 0,
        resetTime: entry.resetTime,
        retryAfter: Math.ceil((entry.blockUntil - now) / 1000)
      }
    }
    
    // Reset if window has expired
    if (now > entry.resetTime) {
      entry.count = 0
      entry.resetTime = now + config.windowMs
      entry.blocked = false
      delete entry.blockUntil
    }
    
    // Check if limit exceeded
    if (entry.count >= config.maxAttempts) {
      // Block if configured
      if (config.blockDurationMs) {
        entry.blocked = true
        entry.blockUntil = now + config.blockDurationMs
      }
      
      return {
        allowed: false,
        remaining: 0,
        resetTime: entry.resetTime,
        retryAfter: config.blockDurationMs ? Math.ceil(config.blockDurationMs / 1000) : undefined
      }
    }
    
    // Increment counter
    entry.count++
    
    return {
      allowed: true,
      remaining: Math.max(0, config.maxAttempts - entry.count),
      resetTime: entry.resetTime
    }
  }

  recordAttempt(
    identifier: string, 
    type: 'login' | 'api' | 'form' | 'file' | 'general',
    options?: Partial<RateLimitOptions>
  ): { allowed: boolean; remaining: number; resetTime: number; retryAfter?: number } {
    return this.isAllowed(identifier, type, options)
  }

  private getConfig(
    type: 'login' | 'api' | 'form' | 'file' | 'general',
    options?: Partial<RateLimitOptions>
  ): Required<RateLimitOptions> {
    const defaults = this.getDefaultConfig(type)
    return {
      maxAttempts: options?.maxAttempts ?? defaults.maxAttempts,
      windowMs: options?.windowMs ?? defaults.windowMs,
      blockDurationMs: options?.blockDurationMs ?? defaults.blockDurationMs,
      keyGenerator: options?.keyGenerator ?? defaults.keyGenerator
    }
  }

  private getDefaultConfig(type: 'login' | 'api' | 'form' | 'file' | 'general'): Required<RateLimitOptions> {
    switch (type) {
      case 'login':
        return {
          maxAttempts: SECURITY_CONFIG.RATE_LIMITS.LOGIN_ATTEMPTS,
          windowMs: SECURITY_CONFIG.RATE_LIMITS.LOGIN_WINDOW,
          blockDurationMs: 15 * 60 * 1000, // 15 minutes
          keyGenerator: (id) => `login:${id}`
        }
      case 'api':
        return {
          maxAttempts: SECURITY_CONFIG.RATE_LIMITS.API_REQUESTS,
          windowMs: SECURITY_CONFIG.RATE_LIMITS.API_WINDOW,
          blockDurationMs: 5 * 60 * 1000, // 5 minutes
          keyGenerator: (id) => `api:${id}`
        }
      case 'form':
        return {
          maxAttempts: SECURITY_CONFIG.RATE_LIMITS.FORM_SUBMISSIONS,
          windowMs: SECURITY_CONFIG.RATE_LIMITS.FORM_WINDOW,
          blockDurationMs: 2 * 60 * 1000, // 2 minutes
          keyGenerator: (id) => `form:${id}`
        }
      case 'file':
        return {
          maxAttempts: SECURITY_CONFIG.RATE_LIMITS.FILE_UPLOADS,
          windowMs: SECURITY_CONFIG.RATE_LIMITS.FILE_WINDOW,
          blockDurationMs: 10 * 60 * 1000, // 10 minutes
          keyGenerator: (id) => `file:${id}`
        }
      default:
        return {
          maxAttempts: 10,
          windowMs: 60 * 1000, // 1 minute
          blockDurationMs: 5 * 60 * 1000, // 5 minutes
          keyGenerator: (id) => `general:${id}`
        }
    }
  }

  // Reset rate limit for specific identifier
  reset(identifier: string, type: 'login' | 'api' | 'form' | 'file' | 'general'): void {
    const key = this.generateKey(identifier, type)
    this.limits.delete(key)
  }

  // Get current status for identifier
  getStatus(identifier: string, type: 'login' | 'api' | 'form' | 'file' | 'general'): {
    count: number
    remaining: number
    resetTime: number
    blocked: boolean
    blockUntil?: number
  } {
    const key = this.generateKey(identifier, type)
    const entry = this.limits.get(key)
    const config = this.getConfig(type)
    
    if (!entry) {
      return {
        count: 0,
        remaining: config.maxAttempts,
        resetTime: Date.now() + config.windowMs,
        blocked: false
      }
    }
    
    const now = Date.now()
    const remaining = Math.max(0, config.maxAttempts - entry.count)
    
    return {
      count: entry.count,
      remaining,
      resetTime: entry.resetTime,
      blocked: entry.blocked || false,
      blockUntil: entry.blockUntil
    }
  }

  // Get all active limits (for monitoring)
  getAllLimits(): Array<{
    key: string
    type: string
    identifier: string
    count: number
    remaining: number
    resetTime: number
    blocked: boolean
    blockUntil?: number
  }> {
    const now = Date.now()
    const results: Array<{
      key: string
      type: string
      identifier: string
      count: number
      remaining: number
      resetTime: number
      blocked: boolean
      blockUntil?: number
    }> = []
    
    for (const [key, entry] of this.limits.entries()) {
      if (now <= entry.resetTime) {
        const [type, identifier] = key.split(':', 2)
        const config = this.getConfig(type as any)
        const remaining = Math.max(0, config.maxAttempts - entry.count)
        
        results.push({
          key,
          type,
          identifier,
          count: entry.count,
          remaining,
          resetTime: entry.resetTime,
          blocked: entry.blocked || false,
          blockUntil: entry.blockUntil
        })
      }
    }
    
    return results
  }

  // Clear all limits
  clearAll(): void {
    this.limits.clear()
  }

  // Destroy the rate limiter (cleanup)
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval)
      this.cleanupInterval = null
    }
    this.limits.clear()
  }
}

// Export singleton instance
export const rateLimiter = RateLimiter.getInstance()

// Export class for testing
export { RateLimiter }

// Convenience functions
export function checkRateLimit(
  identifier: string, 
  type: 'login' | 'api' | 'form' | 'file' | 'general',
  options?: Partial<RateLimitOptions>
): { allowed: boolean; remaining: number; resetTime: number; retryAfter?: number } {
  return rateLimiter.isAllowed(identifier, type, options)
}

export function recordRateLimit(
  identifier: string, 
  type: 'login' | 'api' | 'form' | 'file' | 'general',
  options?: Partial<RateLimitOptions>
): { allowed: boolean; remaining: number; resetTime: number; retryAfter?: number } {
  return rateLimiter.recordAttempt(identifier, type, options)
}

export function resetRateLimit(
  identifier: string, 
  type: 'login' | 'api' | 'form' | 'file' | 'general'
): void {
  rateLimiter.reset(identifier, type)
}

export function getRateLimitStatus(
  identifier: string, 
  type: 'login' | 'api' | 'form' | 'file' | 'general'
): {
  count: number
  remaining: number
  resetTime: number
  blocked: boolean
  blockUntil?: number
} {
  return rateLimiter.getStatus(identifier, type)
}
